import React from 'react'
import { createRoot, Root } from 'react-dom/client'

import { MODAL_EXIT_MS } from './constants'
import {
  ImperativeConfirm,
  ImperativeConfig,
  ImperativeType,
} from './ImperativeConfirm'
import {
  destroyAllShadcnModals,
  registerModal,
  unregisterModal,
  type LiveModal,
} from './registry'

/* -------------------------------------------------------------------------
 * The shadcn half of `Modal.confirm/info/success/error/warning`, and of
 * `Modal.useModal()`.
 *
 * Each call mounts its own React root into a body-level div and returns antd's
 * `{ destroy, update }` handle. Only `./index.tsx` imports this file, and only
 * on the shadcn branch — with the toggle off, antd's own statics run untouched.
 *
 * Ported from the `update-design` reference branch (`src/antd/Modal/
 * imperative.tsx`).
 * ------------------------------------------------------------------------- */

export type ModalFuncReturn = {
  destroy: () => void
  update: (
    next:
      | Partial<ImperativeConfig>
      | ((prev: ImperativeConfig) => Partial<ImperativeConfig>)
  ) => void
}

function spawn(
  type: ImperativeType,
  config: ImperativeConfig
): ModalFuncReturn {
  const div = document.createElement('div')
  document.body.appendChild(div)
  const root: Root = createRoot(div)

  let current: ImperativeConfig = { ...config }
  let destroyed = false

  // `render` and `close` are mutually recursive; declare `close` first (a `let`
  // assigned below) and call it lazily so neither is used before defined. The
  // registry entry is declared the same way, for the same reason.
  let close: () => void
  let entry: LiveModal

  const unmount = () => {
    try {
      root.unmount()
    } catch {
      // The host emptied `<body>` under us (jsdom tests do), so React's own
      // nodes are already gone and it throws removing them. Nothing to undo.
    }
    if (div.parentNode) div.parentNode.removeChild(div)
  }

  const teardown = () => {
    if (destroyed) return
    destroyed = true
    unregisterModal(entry)
    // Defer unmount so we don't unmount during React's render/commit phase.
    setTimeout(unmount, 0)
  }

  // The bulk `destroyAll()` path: immediate and timer-independent, because it
  // has to work under `vi.useFakeTimers()`, where neither the exit animation
  // nor the deferred unmount above would ever run.
  const dispose = () => {
    if (destroyed) return
    destroyed = true
    unregisterModal(entry)
    unmount()
  }

  const render = (open: boolean) => {
    if (destroyed) return
    root.render(
      <ImperativeConfirm
        type={type}
        open={open}
        config={current}
        onClose={() => close()}
      />
    )
  }

  close = () => {
    render(false)
    setTimeout(teardown, MODAL_EXIT_MS)
  }

  entry = { dispose }
  registerModal(entry)

  const update: ModalFuncReturn['update'] = (next) => {
    current = {
      ...current,
      ...(typeof next === 'function' ? next(current) : next),
    }
    render(true)
  }

  render(true)

  return { destroy: close, update }
}

export type ModalStaticFn = (config: ImperativeConfig) => ModalFuncReturn

export type ModalStatics = {
  confirm: ModalStaticFn
  info: ModalStaticFn
  success: ModalStaticFn
  error: ModalStaticFn
  warning: ModalStaticFn
  destroyAll: () => void
}

export const shadcnModalStatics: ModalStatics = {
  confirm: (config) => spawn('confirm', config),
  info: (config) => spawn('info', config),
  success: (config) => spawn('success', config),
  error: (config) => spawn('error', config),
  warning: (config) => spawn('warning', config),
  destroyAll: destroyAllShadcnModals,
}

/**
 * antd's `Modal.useModal()` returns `[instance, contextHolder]`. This codebase
 * does not use it today, but the API is preserved: the instance methods behave
 * like the statics and the context holder is a no-op node (the seam renders
 * into its own root rather than a React-context portal).
 */
export function useShadcnModal(): [ModalStatics, React.ReactElement] {
  return [shadcnModalStatics, <React.Fragment key="modal-holder" />]
}
